import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { getCategoryBySlug } from '@/mocks/articles';

type Props = {
  article: Article;
  variant?: 'default' | 'horizontal' | 'compact';
};

const formatViews = (views: number) => {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return `${views}`;
};

export default function ArticleCard({ article, variant = 'default' }: Props) {
  const category = getCategoryBySlug(article.category);

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex items-center gap-3 rounded-lg p-2 transition-colors hover:bg-background-100"
      >
        <div className="h-16 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 看料99`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0">
          <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <p className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span>{category?.name}</span>
            <span aria-hidden="true">·</span>
            <span>{formatViews(article.views)} 阅读</span>
          </p>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group grid grid-cols-1 overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200 sm:grid-cols-[220px_1fr]"
      >
        <div className="h-44 w-full overflow-hidden bg-background-200 sm:h-full">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 看料99`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="p-4 md:p-5">
          <span className="inline-flex items-center rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-900">
            {category?.name}
          </span>
          <h3 className="mt-2 line-clamp-2 text-lg font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
            {article.title}
          </h3>
          <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">
            {article.summary}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" aria-hidden="true"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true"></i>
              {article.publishedAt}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" aria-hidden="true"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <div className="h-48 w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 看料99`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <span className="inline-flex w-fit items-center rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
          {category?.name}
        </span>
        <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-4 flex items-center justify-between text-xs text-foreground-500">
          <span>{article.publishedAt}</span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" aria-hidden="true"></i>
            {formatViews(article.views)}
          </span>
        </div>
      </div>
    </Link>
  );
}